import { DependencyList, useCallback, useEffect, useRef, useState } from 'react';

export interface IUseLatestRequest<T> {
  data: T;
  loading: boolean;
  error: boolean;
  retry: () => Promise<void>;
}

export const useLatestRequest = <T>(
  request: () => Promise<T>,
  deps: DependencyList,
  initialData: T
): IUseLatestRequest<T> => {
  const [data, setData] = useState<T>(initialData);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const requestIdRef = useRef<number>(0);

  const execute = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    const isCurrent = () => requestIdRef.current === requestId;

    setLoading(true);
    setError(false);
    try {
      const response = await request();
      if (isCurrent()) setData(response ?? initialData);
    } catch {
      if (isCurrent()) setError(true);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    void execute();
  }, [execute]);

  return { data, loading, error, retry: execute };
};

export default useLatestRequest;
